/* eslint-disable react/prop-types */
import {
  Button,
  IconButton,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Card,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Close as CloseIcon,
  Category as CategoryIcon,
  CheckCircleOutline as CheckIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

function SelectedPrecautionsPopup({
  open,
  onClose,
  selectedPrecautions,
  onRemovePrecaution,
  onClearAll,
  precautionOptions = [], // Array of {label: categoryName, options: [{value, label}]}
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  // Group selected precautions by category
  const groupedPrecautions = {};
  let globalIndex = 0;

  // Iterate through precautionOptions to maintain category order
  precautionOptions.forEach((categoryGroup) => {
    const categoryName = categoryGroup.label;
    const precautionsInCategory = [];

    categoryGroup.options.forEach((option) => {
      const selectedPrecaution = selectedPrecautions.find(
        (p) => p.value === option.value
      );
      if (selectedPrecaution) {
        precautionsInCategory.push({
          ...selectedPrecaution,
          globalIndex: globalIndex++,
        });
      }
    });

    if (precautionsInCategory.length > 0) {
      groupedPrecautions[categoryName] = precautionsInCategory;
    }
  });

  // Handle any precautions that don't match a category (shouldn't happen, but just in case)
  const categorizedValues = Object.values(groupedPrecautions)
    .flat()
    .map((p) => p.value);
  const uncategorized = selectedPrecautions.filter(
    (p) => !categorizedValues.includes(p.value)
  );
  if (uncategorized.length > 0) {
    groupedPrecautions["Other"] = uncategorized.map((p) => ({
      ...p,
      globalIndex: globalIndex++,
    }));
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`,
          borderBottom: 1,
          borderColor: theme.palette.divider,
          pb: 2,
          pt: 2.5,
          px: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
            }}
          >
            <CategoryIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 0.5 }}>
              {t("hazards.selectedPrecautionsTitle")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CheckIcon sx={{ fontSize: 16, color: "success.main" }} />
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight="500"
              >
                {t("hazards.selectedPrecautionsSubtitle", {
                  count: selectedPrecautions.length,
                })}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              bgcolor: alpha(theme.palette.text.primary, 0.05),
              "&:hover": {
                bgcolor: alpha(theme.palette.text.primary, 0.1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 3,
          bgcolor: alpha(theme.palette.background.default, 0.4),
        }}
      >
        {selectedPrecautions.length > 0 ? (
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.01)} 100%)`,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight="600"
                color="success.main"
              >
                {selectedPrecautions.length}{" "}
                {selectedPrecautions.length === 1
                  ? "Precaution"
                  : "Precautions"}{" "}
                Selected
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {Object.entries(groupedPrecautions).map(
                  ([categoryName, precautions]) => (
                    <Box key={categoryName}>
                      {/* Category Header */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 4,
                            height: 20,
                            bgcolor: "success.main",
                            borderRadius: 1,
                          }}
                        />
                        <Typography
                          variant="subtitle2"
                          fontWeight="700"
                          color="text.primary"
                          sx={{ flex: 1 }}
                        >
                          {categoryName}
                        </Typography>
                        <Chip
                          label={precautions.length}
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.success.main, 0.1),
                            color: "success.dark",
                            fontWeight: "bold",
                            height: 24,
                            "& .MuiChip-label": {
                              px: 1.5,
                            },
                          }}
                        />
                      </Box>

                      {/* Precautions in this category */}
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                        {precautions.map((precaution) => (
                          <Chip
                            key={precaution.value}
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    bgcolor: alpha(
                                      theme.palette.success.main,
                                      0.15
                                    ),
                                    color: "success.dark",
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: 1,
                                    fontWeight: "bold",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  {precaution.globalIndex + 1}
                                </Typography>
                                <Typography variant="body2" fontWeight="500">
                                  {precaution.label}
                                </Typography>
                              </Box>
                            }
                            onDelete={() =>
                              onRemovePrecaution(precaution.value)
                            }
                            color="success"
                            variant="outlined"
                            sx={{
                              height: 40,
                              borderRadius: 2,
                              borderWidth: 1.5,
                              transition: "all 0.2s ease",
                              "&:hover": {
                                borderColor: theme.palette.success.main,
                                bgcolor: alpha(
                                  theme.palette.success.main,
                                  0.08
                                ),
                                transform: "translateY(-1px)",
                                boxShadow: `0 4px 8px ${alpha(theme.palette.success.main, 0.2)}`,
                              },
                              "& .MuiChip-deleteIcon": {
                                fontSize: "1.1rem",
                                color: theme.palette.success.main,
                                "&:hover": {
                                  color: theme.palette.success.dark,
                                },
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )
                )}
              </Box>
            </Box>
          </Card>
        ) : (
          <Card
            elevation={0}
            sx={{
              border: `1px dashed ${theme.palette.divider}`,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.background.paper, 0.5),
            }}
          >
            <Box sx={{ textAlign: "center", py: 6 }}>
              <CategoryIcon
                sx={{
                  fontSize: 64,
                  color: theme.palette.text.disabled,
                  mb: 2,
                  opacity: 0.3,
                }}
              />
              <Typography
                variant="h6"
                color="text.secondary"
                fontWeight="500"
                sx={{ mb: 1 }}
              >
                {t("hazards.noPrecautionsSelected")}
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Select precautions from the dropdown to see them here
              </Typography>
            </Box>
          </Card>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          p: 3,
          gap: 2,
          bgcolor: alpha(theme.palette.background.default, 0.4),
          borderTop: `1px solid ${theme.palette.divider}`,
          justifyContent: "space-between",
        }}
      >
        {selectedPrecautions.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckIcon sx={{ fontSize: 18, color: "success.main" }} />
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight="500"
            >
              {selectedPrecautions.length} precaution
              {selectedPrecautions.length !== 1 ? "s" : ""} ready to apply
            </Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 2, ml: "auto" }}>
          <Button
            onClick={onClose}
            variant="outlined"
            color="inherit"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.25,
              textTransform: "none",
              fontWeight: "medium",
              borderColor: theme.palette.divider,
              "&:hover": {
                borderColor: theme.palette.text.primary,
                bgcolor: alpha(theme.palette.text.primary, 0.04),
              },
            }}
          >
            {t("hazards.close")}
          </Button>
          {selectedPrecautions.length > 0 && (
            <Button
              onClick={onClearAll}
              variant="contained"
              color="error"
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.25,
                textTransform: "none",
                fontWeight: "bold",
                boxShadow: 3,
                "&:hover": {
                  boxShadow: 6,
                  transform: "translateY(-2px)",
                },
              }}
            >
              {t("hazards.clearAll")}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default SelectedPrecautionsPopup;
